import { useLazyQuery } from "@apollo/client";
import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { GET_CITIES } from "@api/queries/suggestions";
import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageTypes } from "@config/cms";
import { useStore } from "@hooks/useZustandProvider";
import roadConnectionSvg from "@images/svg/road-connection.svg";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import Dropzone from "@ui-kit/Dropzone/Dropzone";
import Loading from "@ui-kit/Loading/Loading";
import Tab from "@ui-kit/Tabs/Tab";
import TabContainer from "@ui-kit/Tabs/TabContainer";
import TabContent from "@ui-kit/Tabs/TabContent";
import Tabs from "@ui-kit/Tabs/Tabs";
import TabsPanel from "@ui-kit/Tabs/TabsPanel";

import s from "./OperationsBlock.module.scss";

const OperationsBlock = ({ settings, data, updateField }) => {
  const { locale } = useRouter();
  const { cms, getBlockLocale, user } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { isStaff } = user;
  const { t } = useTranslation(["common"]);
  const { title, subTitle, image } = data;
  const { blockType } = settings.common;
  const [pages, setPages] = useState([]);
  const [coutries, setCoutries] = useState({});

  const [getCities, { loading: citiesLoading }] = useLazyQuery(GET_CITIES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      try {
        const SORTED_COUNTRIES = {
          Estonia: [],
          Latvia: [],
          Lithuania: [],
          Russia: [],
          Poland: [],
          Finland: [],
        };

        pages.forEach((page) => {
          const fromId = page.properties.from;
          const toId = page.properties.to;
          const getCity = (cityId) =>
            data.cities.find((city) => city.CityId === cityId);
          const cityFrom = getCity(fromId);
          const cityTo = getCity(toId);

          if (!cityTo || !cityFrom) {
            if (isStaff) {
              console.log(
                `Page info with city ID: ${fromId} or ${toId}:`,
                page,
              );

              return toast.warn(
                `[Admin] The city (destination) with ID ${cityId} was not received from the backend. See more details in console.`,
              );
            }

            return;
          }

          // if ((!cityTo || !cityFrom) && isStaff) {
          //   if (!cityTo) {
          //     console.log(`Page info with city ID: ${toId}:`, page);
          //     toast.warn(
          //       `The city (TO) with ID ${toId} was not received from the backend.`,
          //     );
          //   }

          //   if (!cityFrom) {
          //     console.log(`Page info with city ID: ${fromId}:`, page);
          //     toast.warn(
          //       `The city (FROM) with ID ${fromId} was not received from the backend.`,
          //     );
          //   }

          //   return;
          // }

          const countryName = cityFrom.BusStops[0].CountryName;
          const departure = cityFrom.CityName;
          const arrival = cityTo.CityName;
          SORTED_COUNTRIES[countryName].push({
            slug: page.data.slug,
            departure,
            arrival,
          });
        });

        setCoutries(SORTED_COUNTRIES);
      } catch (error) {
        console.log("error", error);
      }
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [getAllPagesUser, { loading: pagesLoading }] = useLazyQuery(
    GET_ALL_PAGES_USER,
    {
      context: { clientName: "v2" },
      variables: {
        language: getBlockLocale(locale),
        type: getPageTypes().route,
        pageSize: 1000,
      },
      onCompleted: (data) => {
        try {
          const error = data?.error;

          if (data.error) {
            return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
          }

          const pages = [...(data?.getAllPagesUser || [])];

          setPages(pages);

          let ids = [];
          pages.forEach((page) =>
            ids.push(...[page.properties.from, page.properties.to]),
          );
          ids = [...new Set(ids)].sort((a, b) => a - b);

          getCities({
            variables: {
              lang: getBlockLocale(locale),
              ids,
            },
          });
        } catch (error) {
          console.log("error", error);
        }
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  useEffect(() => {
    getAllPagesUser();
  }, []);

  const src = image?.src || `/static/images/home/maps/${locale}.jpg`;

  return (
    <Container className={s.operations}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          <div className={cn(s.description)}>
            <p>
              <Editor field={{ subTitle }} onChange={updateField} onlyText />
            </p>
          </div>
          <br />
          <div className={s.map}>
            {isEditMode ? (
              <Dropzone
                imageProps={{ fill: true }}
                onDrop={(path) => {
                  updateField({
                    name: "image",
                    value: {
                      ...image,
                      src: path,
                    },
                  });
                }}
                preview={src}
                removeImage={() => {
                  updateField({
                    name: "image",
                    value: {
                      ...image,
                      src: "",
                      alt: "",
                    },
                  });
                }}
              />
            ) : (
              <Image
                className={s.img}
                fill
                src={src}
                alt=""
                style={{
                  objectFit: "contain",
                  objectPosition: "right",
                }}
              />
            )}
          </div>
          {pagesLoading || citiesLoading ? (
            <Loading />
          ) : (
            <Tabs className={s.tabsDirection}>
              <TabsPanel className={s.tabsPanel}>
                {Object.entries(coutries).map(([countryName]) => (
                  <Tab
                    className={s.tab}
                    text={t(countryName)}
                    key={countryName}
                  />
                ))}
              </TabsPanel>
              <TabContainer>
                {Object.entries(coutries).map(([countryName, routes]) => {
                  return (
                    <TabContent className={s.tabContent} key={countryName}>
                      <div className={s.roadConnections}>
                        {routes.length ? (
                          routes.map((route, key) => {
                            const { departure, arrival, slug } = route;
                            return (
                              <Link
                                href={`/routes/${slug}`}
                                className={s.direction}
                                key={key}
                              >
                                <div className={s.point} title={departure}>
                                  {t(departure)}
                                </div>
                                <div className={s.image}>
                                  <Image src={roadConnectionSvg} alt="" />
                                </div>
                                <div
                                  className={cn(s.point, s.to)}
                                  title={arrival}
                                >
                                  {t(arrival)}
                                </div>
                              </Link>
                            );
                          })
                        ) : (
                          <div>No routes :(</div>
                        )}
                      </div>
                    </TabContent>
                  );
                })}
              </TabContainer>
            </Tabs>
          )}
        </section>
      </Container>
    </Container>
  );
};

export default OperationsBlock;
